/**
 * Athletes-roster CSV → normalized rows ready for bulkInsertAthletes().
 *
 * Pure module — no DOM, no Supabase. The bulk-import UI imports
 * parseAthletesCsv() and feeds the result into the preview table; the
 * tests in parse-csv.test.js exercise the full normalization matrix.
 *
 * CSV format:
 *   Required header row. Headers are case-insensitive and whitespace-
 *   tolerant (we normalize to snake_case).
 *
 *   Required columns:  first_name, last_name
 *   Optional columns:  dob (YYYY-MM-DD or M/D/YY[YY]),
 *                      throws_hand (R|L|S, default R),
 *                      joined_date (YYYY-MM-DD or M/D/YY[YY]),
 *                      nickname
 *
 * Behavior:
 *   - Rows missing first_name or last_name are flagged ok=false.
 *   - Invalid throws_hand falls back to 'R' with a warning.
 *   - Unparseable dates become null with a warning (row stays ok).
 *   - Unknown columns are reported in `unknownColumns`; the rows still parse.
 */

import Papa from 'papaparse';

const VALID_THROWS = new Set(['R', 'L', 'S']);
const REQUIRED_COLS = ['first_name', 'last_name'];
const OPTIONAL_COLS = [
  'athlete_age', 'playing_age',
  'dob', 'throws_hand', 'joined_date', 'nickname',
];

export function parseAthletesCsv(csvText) {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, '_'),
  });

  const headers = result.meta?.fields ?? [];
  const knownCols = new Set([...REQUIRED_COLS, ...OPTIONAL_COLS]);
  const unknownColumns = headers.filter((h) => !knownCols.has(h));

  const rows = (result.data ?? []).map((raw) => {
    const row = {
      first_name:  trimOrNull(raw.first_name),
      last_name:   trimOrNull(raw.last_name),
      athlete_age: parseAge(raw.athlete_age),
      playing_age: trimOrNull(raw.playing_age),
      dob:         normalizeDob(raw.dob),
      throws_hand: normalizeThrows(raw.throws_hand),
      joined_date: normalizeDob(raw.joined_date),
      nickname:    trimOrNull(raw.nickname),
    };

    const warnings = [];
    let ok = true;

    if (!row.first_name) { warnings.push('first_name required'); ok = false; }
    if (!row.last_name)  { warnings.push('last_name required');  ok = false; }
    if (raw.throws_hand && !VALID_THROWS.has(String(raw.throws_hand).trim().toUpperCase())) {
      warnings.push(`throws_hand "${raw.throws_hand}" invalid (must be R/L/S) — defaulting to R`);
    }
    if (raw.athlete_age && row.athlete_age == null) {
      warnings.push(`athlete_age "${raw.athlete_age}" not a valid number — leaving blank`);
    }
    if (raw.dob && !row.dob) {
      warnings.push(`dob "${raw.dob}" not parseable — leaving blank`);
    }
    if (raw.joined_date && !row.joined_date) {
      warnings.push(`joined_date "${raw.joined_date}" not parseable — leaving blank`);
    }

    return { row, ok, warnings };
  });

  return { rows, unknownColumns };
}

function trimOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function normalizeThrows(v) {
  if (!v) return 'R';
  const upper = String(v).trim().toUpperCase();
  if (VALID_THROWS.has(upper)) return upper;
  return 'R';
}

// Athlete age is a small positive integer; sanity-check the range and
// return null for anything that can't reasonably be a person's age.
function parseAge(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = parseInt(String(v).trim(), 10);
  if (!Number.isFinite(n)) return null;
  if (n < 4 || n > 70) return null;
  return n;
}

// Accept YYYY-MM-DD or M/D/YY[YY]; return YYYY-MM-DD or null.
function normalizeDob(v) {
  if (!v) return null;
  const s = String(v).trim();
  if (!s) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (us) {
    const m = parseInt(us[1], 10);
    const d = parseInt(us[2], 10);
    const yRaw = parseInt(us[3], 10);
    const y = us[3].length === 2 ? (yRaw < 50 ? 2000 + yRaw : 1900 + yRaw) : yRaw;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    return `${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
  }
  return null;
}
